// Real-time chat service using Firebase RTDB for messaging, typing, and presence.
// Firebase Storage for file/image uploads.

import { rtdb, storage, auth } from './config';
import {
  ref, push, set, onValue, onDisconnect, serverTimestamp,
  query, orderByChild, limitToLast, off, remove, get,
  DataSnapshot,
} from 'firebase/database';
import {
  ref as storageRef, uploadBytes, getDownloadURL,
} from 'firebase/storage';

export interface ChatMessageData {
  id: string;
  text: string;
  senderId: string;
  senderName: string;
  timestamp: number;
  channelId: string;
  hasLgpdWarning: boolean;
  patientLinked?: string;
  linkedPatientId?: string;
  mentions?: string[];
  type?: 'text' | 'image' | 'file' | 'attention';
  fileUrl?: string;
  fileName?: string;
  fileSize?: number;
  // "Responder a": referência à mensagem original + um SNAPSHOT do autor/preview
  // dela. Guardamos o snapshot (em vez de só o id) para a citação continuar
  // legível mesmo que a original já tenha saído da janela carregada ou tenha
  // sido apagada depois. `replyToId` permite pular para a original quando ela
  // ainda está na tela.
  replyToId?: string;
  replyToSenderName?: string;
  replyToText?: string;
}

/**
 * Contexto de "responder a uma mensagem". Snapshot leve da mensagem original,
 * anexado à resposta para o outro usuário identificar a que pergunta ela se
 * refere. Montado por {@link buildReplyContext}.
 */
export interface ReplyContext {
  replyToId: string;
  replyToSenderName: string;
  replyToText: string;
}

/**
 * Monta o contexto de resposta a partir da mensagem original. Imagens/arquivos/
 * pedidos de atenção viram um rótulo curto (não dá para citar o binário) e
 * textos longos são truncados — a citação é só uma dica visual do que está sendo
 * respondido.
 */
export function buildReplyContext(message: ChatMessageData): ReplyContext {
  let preview: string;
  if (message.type === 'image') preview = '📷 Imagem';
  else if (message.type === 'file') preview = '📎 Arquivo';
  else if (message.type === 'attention') preview = '🔔 Pedido de atenção';
  else preview = message.text || '';
  if (preview.length > 180) preview = `${preview.slice(0, 180)}…`;
  return {
    replyToId: message.id,
    replyToSenderName: message.senderName,
    replyToText: preview,
  };
}

export interface ChatChannelData {
  id: string;
  name: string;
  type: string;
  createdAt: number;
  createdBy?: string;
  members: Record<string, boolean>;
  /**
   * Map uid → displayName para canais de DM. Permite que cada lado da conversa
   * mostre o nome do OUTRO participante sem precisar resolver outra subscription.
   */
  dmParticipants?: Record<string, string>;
}

export interface ClinicMemberData {
  id: string;
  name: string;
  joinedAt: number;
}

/**
 * Uma mensagem marcada como "pendente de resposta". É COMPARTILHADA por
 * conversa: vive em chat/{clinicId}/pending/{channelId}/{messageId} e guarda
 * um snapshot do conteúdo da mensagem, para que a lista de pendências não
 * dependa da janela de mensagens carregada (a original pode já ter saído das
 * últimas N). `messageId`/`channelId` apontam de volta para a mensagem real.
 */
export interface PendingMessageData {
  channelId: string;
  messageId: string;
  text: string;
  senderId: string;
  senderName: string;
  messageTimestamp: number;
  type: 'text' | 'image' | 'file' | 'attention';
  markedAt: number;
  markedBy: string;
  markedByName: string;
}

export type MessageListener = (messages: ChatMessageData[]) => void;
export type ChannelListener = (channels: ChatChannelData[]) => void;
export type ClinicMembersListener = (members: ClinicMemberData[]) => void;
export type TypingListener = (typingUsers: Record<string, { userId: string; userName: string; timestamp: number }>) => void;
export type PresenceListener = (onlineUsers: Record<string, boolean>) => void;

// Default channels auto-created when a clinic first opens the chat
const DEFAULT_CHANNELS: Array<{ id: string; name: string; type: string }> = [
  { id: 'geral', name: 'geral', type: 'channel' },
  { id: 'recepcao', name: 'recepção', type: 'channel' },
  { id: 'cirurgias', name: 'cirurgias', type: 'channel' },
];

function slugifyChannelId(name: string): string {
  const slug = name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  return slug || `channel-${Date.now()}`;
}

/**
 * Reexecuta uma escrita do RTDB que falhou por PERMISSION_DENIED depois de
 * forçar a renovação do token de autenticação.
 *
 * Sintoma que isso resolve: o DRAtento costuma ficar aberto o dia todo (ou a
 * aba é restaurada dias depois). O ID token do Firebase expira a cada ~1h; se
 * a aba ficou suspensa, a primeira escrita ao voltar pega a janela ANTES do
 * refresh automático e o servidor responde PERMISSION_DENIED — mesmo o usuário
 * sendo membro da clínica e do canal (regras e membership intactos). Forçar
 * getIdToken(true) renova a credencial; damos um instante pro RTDB propagar o
 * token novo na conexão e tentamos de novo. Se o refresh em si falhar (sessão
 * de fato revogada — precisa relogar), propaga o erro original pro chamador.
 */
async function withFreshAuthRetry<T>(op: () => Promise<T>): Promise<T> {
  try {
    return await op();
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (!/permission_denied/i.test(msg) || !auth.currentUser) throw err;
    await auth.currentUser.getIdToken(true);
    await new Promise((resolve) => setTimeout(resolve, 400));
    return op();
  }
}

// T195: Real-time message delivery using RTDB onValue listeners
export const ChatService = {
  /**
   * Send a message to a channel. Writes to chat/{clinicId}/messages/{channelId}/{messageId}
   */
  async sendMessage(
    clinicId: string,
    channelId: string,
    text: string,
    senderId: string,
    senderName: string,
    hasLgpdWarning: boolean,
    options?: {
      mentions?: string[];
      linkedPatientId?: string;
      patientLinked?: string;
      type?: 'text' | 'image' | 'file' | 'attention';
      fileUrl?: string;
      fileName?: string;
      fileSize?: number;
      replyToId?: string;
      replyToSenderName?: string;
      replyToText?: string;
    }
  ): Promise<string> {
    const messagesRef = ref(rtdb, `chat/${clinicId}/messages/${channelId}`);
    const newMsgRef = push(messagesRef);

    const messageData: Omit<ChatMessageData, 'id'> = {
      text,
      senderId,
      senderName,
      // Hora do SERVIDOR, não do relógio do cliente. A regra do RTDB exige
      // `timestamp <= now`; um PC com relógio adiantado (mesmo 2s) gravava um
      // timestamp "no futuro" e o servidor recusava a escrita inteira com
      // PERMISSION_DENIED. serverTimestamp() é resolvido no servidor, então
      // nunca fica à frente do `now` da regra — independe do relógio local.
      timestamp: serverTimestamp() as unknown as number,
      channelId,
      hasLgpdWarning,
      type: options?.type || 'text',
      ...(options?.mentions && { mentions: options.mentions }),
      ...(options?.linkedPatientId && { linkedPatientId: options.linkedPatientId }),
      ...(options?.patientLinked && { patientLinked: options.patientLinked }),
      ...(options?.fileUrl && { fileUrl: options.fileUrl }),
      ...(options?.fileName && { fileName: options.fileName }),
      ...(options?.fileSize && { fileSize: options.fileSize }),
      // Grava os 3 campos de resposta juntos (ou nenhum) para o snapshot da
      // citação ficar sempre consistente. RTDB rejeita `undefined`, por isso
      // usamos '' como fallback dos previews.
      ...(options?.replyToId && {
        replyToId: options.replyToId,
        replyToSenderName: options.replyToSenderName ?? '',
        replyToText: options.replyToText ?? '',
      }),
    };

    await withFreshAuthRetry(() => set(newMsgRef, messageData));
    return newMsgRef.key!;
  },

  /**
   * Envia uma mensagem de "pedir atenção" — tipo especial que dispara
   * alerta sonoro + visual mais enfático nos clientes receptores.
   * O texto vai padronizado ("🔔 pediu sua atenção") mas o receptor
   * decide como renderizar.
   */
  async sendAttention(
    clinicId: string,
    channelId: string,
    senderId: string,
    senderName: string,
  ): Promise<string> {
    return this.sendMessage(
      clinicId,
      channelId,
      '🔔 pediu sua atenção',
      senderId,
      senderName,
      false,
      { type: 'attention' },
    );
  },

  /**
   * Subscribe to real-time messages in a channel.
   * Returns an unsubscribe function.
   */
  subscribeToMessages(
    clinicId: string,
    channelId: string,
    callback: MessageListener,
    messageLimit: number = 50
  ): () => void {
    const messagesRef = query(
      ref(rtdb, `chat/${clinicId}/messages/${channelId}`),
      orderByChild('timestamp'),
      limitToLast(messageLimit)
    );

    const handler = (snapshot: DataSnapshot) => {
      const messages: ChatMessageData[] = [];
      snapshot.forEach((child) => {
        messages.push({ id: child.key!, ...child.val() });
      });
      callback(messages);
    };

    onValue(messagesRef, handler);

    return () => off(messagesRef, 'value', handler);
  },

  /**
   * Create a new chat channel. Includes required `name` and `type` fields
   * per RTDB validation rules, plus a boolean membership map so that anyone
   * listed can read/write messages in the channel.
   */
  async createChannel(
    clinicId: string,
    name: string,
    creatorId: string,
    additionalMembers: string[] = [],
    type: string = 'channel',
  ): Promise<string> {
    const baseId = slugifyChannelId(name);
    let channelId = baseId;

    // Avoid clobbering an existing channel with the same slug
    const existingSnap = await get(ref(rtdb, `chat/${clinicId}/channels/${channelId}`));
    if (existingSnap.exists()) {
      channelId = `${baseId}-${Date.now().toString(36)}`;
    }

    const membersMap: Record<string, boolean> = { [creatorId]: true };
    for (const memberId of additionalMembers) {
      if (memberId) membersMap[memberId] = true;
    }

    await set(ref(rtdb, `chat/${clinicId}/channels/${channelId}`), {
      name,
      type,
      createdAt: Date.now(),
      createdBy: creatorId,
      members: membersMap,
    });

    return channelId;
  },

  /**
   * Subscribe to the live list of channels for a clinic.
   */
  subscribeToChannels(clinicId: string, callback: ChannelListener): () => void {
    const channelsRef = ref(rtdb, `chat/${clinicId}/channels`);

    const handler = (snapshot: DataSnapshot) => {
      const channels: ChatChannelData[] = [];
      snapshot.forEach((child) => {
        const val = child.val();
        if (val && val.name) {
          channels.push({
            id: child.key!,
            name: val.name,
            type: val.type || 'channel',
            createdAt: val.createdAt || 0,
            members: val.members || {},
            ...(val.createdBy && { createdBy: val.createdBy }),
            ...(val.dmParticipants && { dmParticipants: val.dmParticipants }),
          });
        }
      });
      channels.sort((a, b) => a.createdAt - b.createdAt);
      callback(channels);
    };

    onValue(channelsRef, handler);
    return () => off(channelsRef, 'value', handler);
  },

  /**
   * Subscribe à lista de membros do chat da clínica (uid → nome).
   * Usado para popular o seletor de "Nova conversa privada".
   */
  subscribeToClinicMembers(clinicId: string, callback: ClinicMembersListener): () => void {
    const membersRef = ref(rtdb, `chat/${clinicId}/members`);

    const handler = (snapshot: DataSnapshot) => {
      const members: ClinicMemberData[] = [];
      snapshot.forEach((child) => {
        const val = child.val();
        if (val && val.name) {
          members.push({
            id: child.key!,
            name: val.name,
            joinedAt: val.joinedAt || 0,
          });
        }
      });
      members.sort((a, b) => a.name.localeCompare(b.name));
      callback(members);
    };

    onValue(membersRef, handler);
    return () => off(membersRef, 'value', handler);
  },

  /**
   * Abre (ou cria) um canal de mensagem direta 1:1 entre dois usuários.
   * O ID é determinístico (`dm-<uidA>-<uidB>` em ordem alfabética dos uids),
   * então os dois lados resolvem o mesmo canal sem coordenação extra.
   *
   * Idempotente: se o canal já existe, só retorna o ID. Caso contrário, cria
   * com `type='dm'`, membership dos dois uids e `dmParticipants` guardando o
   * nome de cada lado para exibição.
   */
  async openDirectMessage(
    clinicId: string,
    currentUserId: string,
    currentUserName: string,
    otherUserId: string,
    otherUserName: string,
  ): Promise<string> {
    if (currentUserId === otherUserId) {
      throw new Error('Não é possível abrir uma conversa privada consigo mesmo.');
    }
    const [a, b] = [currentUserId, otherUserId].sort();
    const channelId = `dm-${a}-${b}`;
    const channelRef = ref(rtdb, `chat/${clinicId}/channels/${channelId}`);

    try {
      const snap = await get(channelRef);
      if (snap.exists()) {
        // Garante que ambos continuam membros (caso alguém tenha saído)
        await set(ref(rtdb, `chat/${clinicId}/channels/${channelId}/members/${currentUserId}`), true);
        return channelId;
      }
    } catch {
      // Se não conseguir ler, tenta criar mesmo assim (rules vão validar)
    }

    await set(channelRef, {
      name: 'dm',
      type: 'dm',
      createdAt: Date.now(),
      createdBy: currentUserId,
      members: { [currentUserId]: true, [otherUserId]: true },
      dmParticipants: { [currentUserId]: currentUserName, [otherUserId]: otherUserName },
    });

    return channelId;
  },

  /**
   * Make sure the current user has a membership record at
   * `chat/{clinicId}/members/{userId}`. RTDB rules require this before
   * the user can read or write anything else in the clinic chat tree.
   */
  async ensureClinicMember(clinicId: string, userId: string, userName: string): Promise<void> {
    const memberRef = ref(rtdb, `chat/${clinicId}/members/${userId}`);
    try {
      const snap = await get(memberRef);
      if (snap.exists()) return;
    } catch {
      // If we can't read, still try to write our own entry (rules allow self-write).
    }
    await set(memberRef, {
      name: userName,
      joinedAt: Date.now(),
    });
  },

  /**
   * Ensure the given user is a member of a specific channel.
   */
  async ensureChannelMember(clinicId: string, channelId: string, userId: string): Promise<void> {
    const memberRef = ref(rtdb, `chat/${clinicId}/channels/${channelId}/members/${userId}`);
    try {
      const snap = await get(memberRef);
      if (snap.val() === true) return;
    } catch {
      // continue and attempt to write
    }
    await set(memberRef, true);
  },

  /**
   * Garante que o usuário é membro de TODOS os canais públicos (não-DM) que
   * já existem na clínica. Sem isso, quem entra na clínica DEPOIS dos canais
   * default terem sido seedados (ex: secretária quando o médico já criou) só
   * vira membro do canal ao trocar para ele manualmente — e até lá, regras
   * de RTDB bloqueiam `subscribeToMessages`, então nenhum alerta dispara.
   * Idempotente: se já é membro, o set vira no-op.
   */
  async joinAllPublicChannels(clinicId: string, userId: string): Promise<void> {
    try {
      const channelsSnap = await get(ref(rtdb, `chat/${clinicId}/channels`));
      const writes: Promise<void>[] = [];
      channelsSnap.forEach((child) => {
        const val = child.val();
        if (val && val.type !== 'dm') {
          writes.push(
            set(
              ref(rtdb, `chat/${clinicId}/channels/${child.key}/members/${userId}`),
              true,
            ),
          );
        }
      });
      await Promise.all(writes);
    } catch (err) {
      // Não bloqueia o bootstrap se um canal específico falhar
      console.warn('joinAllPublicChannels parcialmente falhou:', err);
    }
  },

  /**
   * Seed the default channels (geral, recepcao, cirurgias) for a clinic if
   * no channels exist yet. The creator is automatically added as a member
   * of every default channel.
   */
  async seedDefaultChannels(clinicId: string, creatorId: string): Promise<void> {
    const channelsRef = ref(rtdb, `chat/${clinicId}/channels`);
    const snap = await get(channelsRef);
    if (snap.exists() && snap.hasChildren()) return;

    for (const ch of DEFAULT_CHANNELS) {
      await set(ref(rtdb, `chat/${clinicId}/channels/${ch.id}`), {
        name: ch.name,
        type: ch.type,
        createdAt: Date.now(),
        createdBy: creatorId,
        members: { [creatorId]: true },
      });
    }
  },

  // T196: Typing indicator
  /**
   * Set typing status. Writes to chat/{clinicId}/typing/{channelId}/{userId}.
   * Automatically clears after 3 seconds of inactivity.
   */
  _typingTimers: new Map<string, ReturnType<typeof setTimeout>>(),

  setTyping(clinicId: string, channelId: string, userId: string, userName: string): void {
    const typingRef = ref(rtdb, `chat/${clinicId}/typing/${channelId}/${userId}`);
    set(typingRef, {
      userId,
      userName,
      timestamp: Date.now(),
    });

    // Clear previous timer for this user/channel
    const timerKey = `${clinicId}_${channelId}_${userId}`;
    const existingTimer = this._typingTimers.get(timerKey);
    if (existingTimer) clearTimeout(existingTimer);

    // Auto-clear after 3 seconds
    const timer = setTimeout(() => {
      remove(typingRef);
      this._typingTimers.delete(timerKey);
    }, 3000);

    this._typingTimers.set(timerKey, timer);
  },

  clearTyping(clinicId: string, channelId: string, userId: string): void {
    const typingRef = ref(rtdb, `chat/${clinicId}/typing/${channelId}/${userId}`);
    remove(typingRef);

    const timerKey = `${clinicId}_${channelId}_${userId}`;
    const timer = this._typingTimers.get(timerKey);
    if (timer) {
      clearTimeout(timer);
      this._typingTimers.delete(timerKey);
    }
  },

  subscribeToTyping(clinicId: string, channelId: string, callback: TypingListener): () => void {
    const typingRef = ref(rtdb, `chat/${clinicId}/typing/${channelId}`);

    const handler = (snapshot: DataSnapshot) => {
      const typingUsers: Record<string, { userId: string; userName: string; timestamp: number }> = {};
      snapshot.forEach((child) => {
        const val = child.val();
        // Only include typing indicators from last 5 seconds
        if (Date.now() - val.timestamp < 5000) {
          typingUsers[child.key!] = val;
        }
      });
      callback(typingUsers);
    };

    onValue(typingRef, handler);
    return () => off(typingRef, 'value', handler);
  },

  // T196: Online presence with onDisconnect handler
  /**
   * Set user as online and configure onDisconnect to set offline.
   */
  async setOnlinePresence(clinicId: string, userId: string): Promise<void> {
    const presenceRef = ref(rtdb, `chat/${clinicId}/presence/${userId}`);
    await set(presenceRef, {
      online: true,
      lastSeen: serverTimestamp(),
    });

    // When disconnected, set offline
    await onDisconnect(presenceRef).set({
      online: false,
      lastSeen: serverTimestamp(),
    });
  },

  async setOfflinePresence(clinicId: string, userId: string): Promise<void> {
    const presenceRef = ref(rtdb, `chat/${clinicId}/presence/${userId}`);
    await set(presenceRef, {
      online: false,
      lastSeen: serverTimestamp(),
    });
  },

  subscribeToPresence(clinicId: string, callback: PresenceListener): () => void {
    const presenceRef = ref(rtdb, `chat/${clinicId}/presence`);

    const handler = (snapshot: DataSnapshot) => {
      const users: Record<string, boolean> = {};
      snapshot.forEach((child) => {
        const val = child.val();
        users[child.key!] = val?.online ?? false;
      });
      callback(users);
    };

    onValue(presenceRef, handler);
    return () => off(presenceRef, 'value', handler);
  },

  // T200: File/image upload
  /**
   * Upload a file to Firebase Storage and send a message with the download URL.
   * Path: chat/{clinicId}/{channelId}/{filename}
   */
  async uploadFileAndSend(
    clinicId: string,
    channelId: string,
    file: File,
    senderId: string,
    senderName: string,
  ): Promise<string> {
    const timestamp = Date.now();
    const safeName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
    const fileRef = storageRef(storage, `chat/${clinicId}/${channelId}/${safeName}`);

    await uploadBytes(fileRef, file);
    const downloadUrl = await getDownloadURL(fileRef);

    const isImage = file.type.startsWith('image/');
    const messageType: 'image' | 'file' = isImage ? 'image' : 'file';

    const messageId = await this.sendMessage(
      clinicId,
      channelId,
      isImage ? `[Imagem: ${file.name}]` : `[Arquivo: ${file.name}]`,
      senderId,
      senderName,
      false,
      {
        type: messageType,
        fileUrl: downloadUrl,
        fileName: file.name,
        fileSize: file.size,
      }
    );

    return messageId;
  },

  /**
   * Rename an existing channel.
   */
  async renameChannel(clinicId: string, channelId: string, newName: string): Promise<void> {
    const nameRef = ref(rtdb, `chat/${clinicId}/channels/${channelId}/name`);
    await set(nameRef, newName);
  },

  /**
   * Delete a channel and all its messages, typing indicators.
   */
  async deleteChannel(clinicId: string, channelId: string): Promise<void> {
    await remove(ref(rtdb, `chat/${clinicId}/channels/${channelId}`));
    await remove(ref(rtdb, `chat/${clinicId}/messages/${channelId}`));
    await remove(ref(rtdb, `chat/${clinicId}/typing/${channelId}`));
    await remove(ref(rtdb, `chat/${clinicId}/pending/${channelId}`));
  },

  /**
   * Apaga uma mensagem específica. Caller é responsável por checar permissão
   * (autor ou owner da clínica) antes de chamar.
   */
  async deleteMessage(clinicId: string, channelId: string, messageId: string): Promise<void> {
    await remove(ref(rtdb, `chat/${clinicId}/messages/${channelId}/${messageId}`));
    // Limpa a pendência associada, se houver (no-op se não existir).
    await remove(ref(rtdb, `chat/${clinicId}/pending/${channelId}/${messageId}`));
  },

  /**
   * Limpa todo o histórico de mensagens de um canal, mantendo o canal e
   * seus membros. Caller checa permissão antes (criador do canal ou owner
   * da clínica). Também limpa indicadores de digitação para evitar estados
   * orfãos.
   */
  async clearChannelMessages(clinicId: string, channelId: string): Promise<void> {
    await remove(ref(rtdb, `chat/${clinicId}/messages/${channelId}`));
    await remove(ref(rtdb, `chat/${clinicId}/typing/${channelId}`));
    await remove(ref(rtdb, `chat/${clinicId}/pending/${channelId}`));
  },

  /**
   * Remove a member from a channel.
   */
  async removeChannelMember(clinicId: string, channelId: string, userId: string): Promise<void> {
    await remove(ref(rtdb, `chat/${clinicId}/channels/${channelId}/members/${userId}`));
  },

  /**
   * Link a patient to an existing message by updating it.
   */
  async linkPatientToMessage(
    clinicId: string,
    channelId: string,
    messageId: string,
    patientId: string,
    patientName: string,
  ): Promise<void> {
    const messageRef = ref(rtdb, `chat/${clinicId}/messages/${channelId}/${messageId}`);
    // Use set with merge-like behavior by reading + writing
    await set(ref(rtdb, `chat/${clinicId}/messages/${channelId}/${messageId}/linkedPatientId`), patientId);
    await set(ref(rtdb, `chat/${clinicId}/messages/${channelId}/${messageId}/patientLinked`), patientName);
    // Suppress unused variable warning
    void messageRef;
  },

  /**
   * Marca uma mensagem como "pendente de resposta". A pendência é
   * COMPARTILHADA por conversa (qualquer membro do canal vê e pode resolver) e
   * grava um snapshot do conteúdo, então a lista funciona mesmo que a mensagem
   * original já tenha saído da janela de mensagens carregada. Idempotente:
   * marcar de novo só sobrescreve o snapshot.
   */
  async markMessagePending(
    clinicId: string,
    message: ChatMessageData,
    markedBy: string,
    markedByName: string,
  ): Promise<void> {
    const pendingRef = ref(rtdb, `chat/${clinicId}/pending/${message.channelId}/${message.id}`);
    const snapshot = {
      text: message.text,
      senderId: message.senderId,
      senderName: message.senderName,
      messageTimestamp: message.timestamp,
      type: message.type || 'text',
      markedAt: serverTimestamp() as unknown as number,
      markedBy,
      markedByName,
    };
    await withFreshAuthRetry(() => set(pendingRef, snapshot));
  },

  /**
   * Resolve (remove) uma pendência. Qualquer membro do canal pode resolver.
   */
  async resolvePending(clinicId: string, channelId: string, messageId: string): Promise<void> {
    await remove(ref(rtdb, `chat/${clinicId}/pending/${channelId}/${messageId}`));
  },

  /**
   * Subscribe a TODAS as pendências da clínica (chat/{clinicId}/pending).
   * Retorna a lista achatada — o caller filtra pelas conversas que enxerga e
   * agrupa por canal. Returns an unsubscribe function.
   */
  subscribeToPending(
    clinicId: string,
    callback: (items: PendingMessageData[]) => void,
  ): () => void {
    const pendingRef = ref(rtdb, `chat/${clinicId}/pending`);

    const handler = (snapshot: DataSnapshot) => {
      const items: PendingMessageData[] = [];
      snapshot.forEach((channelSnap) => {
        const channelId = channelSnap.key!;
        channelSnap.forEach((msgSnap) => {
          const val = msgSnap.val() || {};
          items.push({
            channelId,
            messageId: msgSnap.key!,
            text: val.text ?? '',
            senderId: val.senderId ?? '',
            senderName: val.senderName ?? '',
            messageTimestamp: val.messageTimestamp ?? 0,
            type: val.type ?? 'text',
            markedAt: val.markedAt ?? 0,
            markedBy: val.markedBy ?? '',
            markedByName: val.markedByName ?? '',
          });
        });
      });
      callback(items);
    };

    onValue(pendingRef, handler);
    return () => off(pendingRef, 'value', handler);
  },
};
